/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword} from '../../utils/password-utils'

export default function useUpdatePasswordFields({form: {control, errors, getValues}, prefix = ''}) {
    const {formatMessage} = useIntl()

    return {
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Email',
                id: 'use_registration_fields.label.email'
            }),
            type: 'email',
            defaultValue: '',
            helpText: formatMessage({
                defaultMessage:
                    'Your email address is linked to your current and past orders and cannot be changed. If you need to use a different email, please create a new account.',
                id: 'mystokke.personal-information.email-helptext'
            }),
            helpTextInfoIcon: true,
            inputProps: {
                disabled: true,
                variant: 'flushed'
            },
            control
        },
        currentPassword: {
            name: `${prefix}currentPassword`,
            label: formatMessage({
                defaultMessage: 'Current password',
                id: 'mystokke.personal-information.current-password'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Your current password',
                id: 'use_update_password_fields.placeholder.current_password'
            }),
            defaultValue: '',
            type: 'password',
            inputProps: {
                autoComplete: 'password'
            },
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your password.',
                    id: 'use_login_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_update_password_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_update_password_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_update_password_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_update_password_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_update_password_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}currentPassword`],
            control
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'New Password',
                id: 'use_update_password_fields.label.new_password'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Your new password',
                id: 'use_update_password_fields.placeholder.new_password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Password must not be empty.',
                    id: 'use_update_password_fields.error.not_empty'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'use_update_password_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'use_update_password_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'use_update_password_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'use_update_password_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'use_update_password_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            control
        },
        confirmPassword: {
            name: `${prefix}confirmPassword`,
            label: formatMessage({
                defaultMessage: 'Confirm password',
                id: 'mystokke.personal-information.password-confirm'
            }),
            placeholder: formatMessage({
                defaultMessage: 'Confirm your new password',
                id: 'use_update_password_fields.placeholder.confirm_password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Password must not be empty.',
                    id: 'use_update_password_fields.error.not_empty'
                }),
                validate: (val) => {
                    if (getValues('password') !== val) {
                        return formatMessage({
                            defaultMessage: 'The two passwords do not match',
                            id: 'use_update_password_fields.error.not_same_password'
                        })
                    }
                }
            },
            error: errors[`${prefix}confirmPassword`],
            control
        }
    }
}
